// Persisted Workflow board filters.
//
// Employee, the category tick boxes and sort survive a visit; SEARCH
// DELIBERATELY DOES NOT. A search box that silently repopulates on return
// hides most of the board while looking like an empty board -- that is the
// standard way this feature goes wrong, and it is worth the inconsistency to
// avoid.
//
// Modelled on pages/invoices/columns.js: try/catch on both sides (private
// browsing and locked-down webviews throw on localStorage access) and unknown
// values discarded rather than applied, so a stale value from an older build
// cannot filter the board down to nothing.
//
// 2026-10-30: the client-type DROPDOWN became tick boxes (see categories.js),
// so `clientType` (one value) became `types` (the ticked category keys). A
// saved clientType from an older build is translated once, on read; it is
// never written again.

import { CATEGORY_KEYS } from "./categories";

export const WORKFLOW_FILTERS_KEY = "workflowFilters";

const SORT_BYS = ["name", "due_date"];

// What the old dropdown's values mean as ticks. "other" listed every Other
// task, which now splits into Bookkeeping + Other. "tslips" never narrowed
// anything (the API has no tslips branch for the board), and T-slips now live
// under Other -- so it becomes the Other box.
const LEGACY_CLIENT_TYPE = {
  all: CATEGORY_KEYS,
  corporate: ["corporate"],
  personal: ["personal"],
  other: ["bookkeeping", "other"],
  tslips: ["other"],
};

const DEFAULTS = { employee: "all", sortBy: "name" };

// Known keys only, in display order. An empty result (nothing ticked, or only
// stale keys) is "no answer" and falls back to everything: a saved empty
// selection would reopen as an empty board that looks like there is no work.
function cleanTypes(value) {
  if (!Array.isArray(value)) return null;
  const picked = CATEGORY_KEYS.filter((k) => value.includes(k));
  return picked.length ? picked : null;
}

export function loadFilterPrefs(currentUserId) {
  // With nothing saved, the board opens on your own work rather than
  // everyone's. An explicitly saved "all" still wins -- see the test.
  const fallbackEmployee =
    currentUserId != null ? String(currentUserId) : DEFAULTS.employee;

  let stored = null;
  try {
    const raw = localStorage.getItem(WORKFLOW_FILTERS_KEY);
    if (raw) stored = JSON.parse(raw);
  } catch (e) {
    stored = null;
  }
  if (!stored || typeof stored !== "object") {
    return { ...DEFAULTS, types: [...CATEGORY_KEYS], employee: fallbackEmployee };
  }

  const types =
    cleanTypes(stored.types) ||
    cleanTypes(LEGACY_CLIENT_TYPE[stored.clientType]) ||
    [...CATEGORY_KEYS];

  return {
    employee:
      typeof stored.employee === "string" && stored.employee
        ? stored.employee
        : fallbackEmployee,
    types,
    sortBy: SORT_BYS.includes(stored.sortBy) ? stored.sortBy : DEFAULTS.sortBy,
  };
}

export function saveFilterPrefs(filters) {
  try {
    localStorage.setItem(
      WORKFLOW_FILTERS_KEY,
      JSON.stringify({
        employee: filters.employee,
        types: filters.types,
        sortBy: filters.sortBy,
      }),
    );
  } catch (e) {
    // Private browsing / quota. Filters still work for this session.
  }
}
